import { useApolloClient } from '@apollo/client';
import type { DealProviderConfig } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import type { DealsListStoreProps } from '@openx/components-deal/lib/utils/context/DealHookTypes.ts';
import type { ListDealBase, OAListDeal } from '@openx/types/deal.ts';
import { QueryKeys } from '@openx/utils/api/queryKeys.ts';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import {
  GetDealLandingDocument,
  type GetDealLandingQuery,
  type GetDealLandingQueryVariables,
} from '../../../graphql/query/deals/GetDealLanding.generated.tsx';
import { findLastPopulatedPage } from '../../../utils/findLastPopulatedPage.ts';
import { resolveDealLandingAccountFilter } from './dealLandingQueryCriteria.ts';
import { serializeDealLandingQueryString } from './serializeDealLandingQueryString.ts';

export const useDealsLandingListFetch: DealProviderConfig['useDealsLandingListFetch'] = <
  T extends ListDealBase = OAListDeal,
>(
  criteria,
  options
) => {
  const apolloClient = useApolloClient();
  const queryClient = useQueryClient();

  const accountTranslation = options?.accountTranslation;
  const accountTranslationReady = options?.accountTranslationReady ?? true;

  const resolvedCriteria = useMemo(
    () => resolveDealLandingAccountFilter(criteria, accountTranslation),
    [criteria, accountTranslation]
  );

  const hasAccountFilter = !!criteria.filters?.byValue?.some(
    filter => filter.field === 'account' && filter.values.length
  );

  const pageSize = resolvedCriteria.pagination?.pageSize ?? 25;

  const queryString = useMemo(() => serializeDealLandingQueryString(resolvedCriteria), [resolvedCriteria]);

  const landingQuery = useQuery({
    queryKey: [QueryKeys.DEAL_LANDING, queryString],
    queryFn: async () => {
      const response = await apolloClient.query<GetDealLandingQuery, GetDealLandingQueryVariables>({
        query: GetDealLandingDocument,
        variables: { queryString },
        fetchPolicy: 'network-only',
      });

      return response.data;
    },
    // Wait for account translation when an Organization filter is active so the
    // translated account_uid values are used. Without an active account filter
    // the request is independent of account loading and can fire immediately.
    enabled: !hasAccountFilter || accountTranslationReady,
    // Keep the previous page visible behind the table loading state while a
    // filter/search refetch is in flight.
    placeholderData: keepPreviousData,
  });

  const data = landingQuery.data;

  const dealsList = useMemo(() => {
    const objects = data?.dealLanding?.objects ?? [];

    return objects.map(dealRaw => ({
      ...dealRaw,
      deal_id: dealRaw.exchange_deal_id,
      deal_participants: (dealRaw.exchange_deal as { deal_participants?: unknown } | null)?.deal_participants,
      synchronized_with: (dealRaw.exchange_deal as { synchronized_with?: unknown } | null)?.synchronized_with,
      uid: dealRaw.exchange_uid,
      dsp_names: dealRaw.dsp_names ?? null,
      buyer_names: dealRaw.buyer_names ?? null,
    }));
  }, [data]);

  // Track the farthest observed end offset so totalCount grows monotonically.
  // Updating a ref does not cause another render.
  const maxObservedRef = useRef(0);

  const totalCount = (() => {
    const landing = data?.dealLanding;

    if (!landing) {
      return maxObservedRef.current;
    }

    const observedEnd = landing.offset + landing.objects.length;

    if (landing.offset === 0) {
      maxObservedRef.current = 0;
    }

    if (observedEnd > maxObservedRef.current) {
      maxObservedRef.current = observedEnd;
    }

    if (!landing.has_more && observedEnd > 0) {
      maxObservedRef.current = observedEnd;
    }

    return maxObservedRef.current + (landing.has_more ? 1 : 0);
  })();

  const hasMore = data?.dealLanding?.has_more ?? false;

  const fetchMore = useCallback(
    (queryCrit: { variables?: { offset?: number } }) => {
      const preloadOffset = queryCrit.variables?.offset;

      if (preloadOffset == null) {
        return;
      }

      const nextQueryString = serializeDealLandingQueryString({
        ...resolvedCriteria,
        pagination: {
          ...resolvedCriteria.pagination,
          pageNumber: Math.floor(preloadOffset / pageSize) + 1,
        },
      });

      void queryClient.prefetchQuery({
        queryKey: [QueryKeys.DEAL_LANDING, nextQueryString],
        queryFn: async () => {
          const response = await apolloClient.query<GetDealLandingQuery, GetDealLandingQueryVariables>({
            query: GetDealLandingDocument,
            variables: { queryString: nextQueryString },
            fetchPolicy: 'network-only',
          });

          return response.data;
        },
      });
    },
    [resolvedCriteria, pageSize, apolloClient, queryClient]
  );

  const resolveLastPopulatedPage = useCallback(
    async (requestedPage: number, abortSignal?: AbortSignal) =>
      findLastPopulatedPage({
        requestedPage,
        abortSignal,

        isPagePopulated: async (candidatePage, signal) => {
          const probeParams = new URLSearchParams(queryString);
          const probeOffset = (candidatePage - 1) * pageSize;

          probeParams.set('limit', '1');

          if (probeOffset > 0) {
            probeParams.set('offset', String(probeOffset));
          } else {
            probeParams.delete('offset');
          }

          const response = await apolloClient.query<GetDealLandingQuery, GetDealLandingQueryVariables>({
            query: GetDealLandingDocument,
            variables: {
              queryString: probeParams.toString(),
            },
            fetchPolicy: 'no-cache',
            context: {
              fetchOptions: {
                signal,
              },
            },
          });

          return response.data.dealLanding.objects.length > 0;
        },
      }),
    [apolloClient, pageSize, queryString]
  );

  return {
    dealsList: dealsList as unknown as T[],
    error: landingQuery.error,
    fetchMore,
    // Include background refetches so the table shows its loading state after
    // search/filter changes. Filters remain interactive while loading.
    loading: landingQuery.isFetching,
    totalCount,
    hasMore,
    resolveLastPopulatedPage,
  } as DealsListStoreProps<T>;
};
